import { useMemo, useState } from "react";
import type { Editor } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import {
  ActionIcon,
  Button,
  Collapse,
  Divider,
  Group,
  Menu,
  Modal,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
} from "@mantine/core";
import { LinkCascadePicker } from "@/features/editor/components/link/link-cascade-picker.tsx";
import {
  IconChevronLeft,
  IconChevronRight,
  IconEye,
  IconEyeOff,
  IconLink,
  IconPhoto,
  IconPlus,
  IconTextSize,
  IconTrash,
  IconPaperclip,
} from "@tabler/icons-react";
import type {
  CardAttrs,
  CardField,
  CardFilesField,
  CardLinkField,
  CardTextField,
} from "@docmost/editor-ext";
import classes from "./cards.module.css";

type Props = {
  opened: boolean;
  onClose: () => void;
  node: PMNode;
  editor: Editor;
  getPos?: (() => number | undefined) | boolean;
  updateAttributes: (attrs: Record<string, any>) => void;
  deleteNode: () => void;
};

function rid(): string {
  return `f${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;
}

export { LinkCascadePicker as LinkFieldInput } from "@/features/editor/components/link/link-cascade-picker.tsx";

function findSiblings(
  editor: Editor,
  getPos: Props["getPos"],
): { siblings: { pos: number; attrs: CardAttrs }[]; index: number } {
  const result = { siblings: [] as { pos: number; attrs: CardAttrs }[], index: 0 };
  if (typeof getPos !== "function") return result;
  const pos = getPos() as number | undefined;
  if (pos == null) return result;
  const $pos = editor.state.doc.resolve(pos);
  const parent = $pos.parent;
  if (parent.type.name !== "cards") return result;
  const parentStart = $pos.start();
  let offset = parentStart;
  let i = 0;
  parent.forEach((child) => {
    if (child.type.name === "card") {
      result.siblings.push({
        pos: offset,
        attrs: child.attrs as CardAttrs,
      });
      if (offset === pos) result.index = i;
      i++;
    }
    offset += child.nodeSize;
  });
  return result;
}

function defaultField(type: CardField["type"]): CardField {
  if (type === "text") {
    return {
      id: rid(),
      type: "text",
      label: "Field",
      value: "",
      style: "default",
      visible: true,
    } as CardTextField;
  }
  if (type === "link") {
    return {
      id: rid(),
      type: "link",
      label: "Link",
      href: "",
      text: "",
      external: true,
      visible: true,
    } as CardLinkField;
  }
  return {
    id: rid(),
    type: "files",
    label: "Files",
    files: [],
    visible: true,
  } as CardFilesField;
}

function focusCardAt(editor: Editor, pos: number) {
  try {
    editor.chain().focus().setNodeSelection(pos).run();
  } catch {
    /* no-op */
  }
}

export default function CardEditModal(props: Props) {
  const { opened, onClose, node, editor, getPos, updateAttributes, deleteNode } =
    props;
  const attrs = node.attrs as CardAttrs;
  const fields = attrs.fields ?? [];
  const visibleFields = fields.filter((f) => f.visible !== false);
  const hiddenFields = fields.filter((f) => f.visible === false);

  const [hiddenOpen, setHiddenOpen] = useState(false);

  const { siblings, index } = useMemo(
    () => findSiblings(editor, getPos),
    // recompute whenever editor state changes while modal is open
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, getPos, opened, editor.state],
  );

  const updateField = (id: string, patch: Partial<CardField>) => {
    const next = fields.map((f) =>
      f.id === id ? ({ ...f, ...patch } as CardField) : f,
    );
    updateAttributes({ fields: next });
  };

  const removeField = (id: string) => {
    updateAttributes({ fields: fields.filter((f) => f.id !== id) });
  };

  const addField = (type: CardField["type"]) => {
    updateAttributes({ fields: [...fields, defaultField(type)] });
  };

  const renderFieldRow = (f: CardField, hidden: boolean) => {
    return (
      <div
        key={f.id}
        className={`${classes.fieldRow} ${hidden ? classes.hiddenFieldRow : ""}`}
      >
        <TextInput
          className={classes.fieldRowInput}
          size="xs"
          placeholder="Label"
          value={f.label ?? ""}
          onChange={(e) =>
            updateField(f.id, { label: e.currentTarget.value } as Partial<CardField>)
          }
        />
        {f.type === "text" ? (
          <TextInput
            className={classes.fieldRowInput}
            size="xs"
            placeholder="Value"
            value={(f as CardTextField).value}
            onChange={(e) =>
              updateField(f.id, { value: e.currentTarget.value } as Partial<CardField>)
            }
          />
        ) : null}
        {f.type === "link" ? (
          <LinkCascadePicker
            value={(f as CardLinkField).href}
            onChange={(href) =>
              updateField(f.id, {
                href,
                external: /^https?:\/\//i.test(href),
              } as Partial<CardField>)
            }
          />
        ) : null}
        {f.type === "files" ? (
          <Text size="xs" c="dimmed">
            {((f as CardFilesField).files ?? []).length} file(s)
          </Text>
        ) : null}
        {f.type === "text" && (
          <Select
            size="xs"
            w={120}
            data={[
              { value: "default", label: "Default" },
              { value: "title", label: "Title" },
              { value: "description", label: "Description" },
            ]}
            value={(f as CardTextField).style ?? "default"}
            onChange={(v) =>
              updateField(f.id, {
                style: (v ?? "default") as CardTextField["style"],
              } as Partial<CardField>)
            }
          />
        )}
        <Tooltip label={hidden ? "Show field" : "Hide field"} withArrow>
          <ActionIcon
            variant="subtle"
            color="gray"
            size="sm"
            onClick={() =>
              updateField(f.id, { visible: hidden ? true : false } as Partial<CardField>)
            }
            aria-label={hidden ? "Show field" : "Hide field"}
          >
            {hidden ? <IconEye size={14} /> : <IconEyeOff size={14} />}
          </ActionIcon>
        </Tooltip>
        <Tooltip label="Delete field" withArrow>
          <ActionIcon
            variant="subtle"
            color="red"
            size="sm"
            onClick={() => removeField(f.id)}
            aria-label="Delete field"
          >
            <IconTrash size={14} />
          </ActionIcon>
        </Tooltip>
      </div>
    );
  };

  const go = (dir: -1 | 1) => {
    const next = index + dir;
    if (next < 0 || next >= siblings.length) return;
    const target = siblings[next];
    focusCardAt(editor, target.pos);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Edit card"
      centered
      size="lg"
    >
      <Stack gap="sm">
        {/* Cover image URL and Target link are set via inline UI on the
            card itself (cover modal + ↗ target-link badge). The modal
            focuses on fields management only. */}

        <Stack gap={6}>
          {visibleFields.length === 0 ? (
            <Text size="xs" c="dimmed">
              No visible fields. Add one below.
            </Text>
          ) : (
            visibleFields.map((f) => renderFieldRow(f, false))
          )}
        </Stack>

        <Group gap="xs">
          <Menu shadow="md" withinPortal>
            <Menu.Target>
              <Button size="xs" leftSection={<IconPlus size={14} />} variant="default">
                Add field
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconTextSize size={14} />}
                onClick={() => addField("text")}
              >
                Text
              </Menu.Item>
              <Menu.Item
                leftSection={<IconPaperclip size={14} />}
                onClick={() => addField("files")}
              >
                Files
              </Menu.Item>
              <Menu.Item
                leftSection={<IconLink size={14} />}
                onClick={() => addField("link")}
              >
                Link
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>

          {hiddenFields.length > 0 && (
            <Button
              size="xs"
              variant="subtle"
              color="gray"
              onClick={() => setHiddenOpen((s) => !s)}
            >
              {hiddenOpen
                ? `Hide hidden fields (${hiddenFields.length})`
                : `Show hidden fields (${hiddenFields.length})`}
            </Button>
          )}
        </Group>

        <Collapse in={hiddenOpen}>
          <Stack gap={6}>
            {hiddenFields.map((f) => renderFieldRow(f, true))}
          </Stack>
        </Collapse>

        <Group gap="xs" mt="xs">
          <Tooltip label={attrs.cover ? "Remove cover" : "Set cover from main menu"} withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              size="sm"
              onClick={() => {
                if (attrs.cover) updateAttributes({ cover: null });
              }}
              disabled={!attrs.cover}
              aria-label="Remove cover"
            >
              <IconPhoto size={14} />
            </ActionIcon>
          </Tooltip>
        </Group>

        <Divider />

        <Group justify="space-between">
          <Button
            size="xs"
            color="red"
            variant="light"
            leftSection={<IconTrash size={14} />}
            onClick={() => {
              deleteNode();
              onClose();
            }}
          >
            Delete card
          </Button>

          <Group gap={4}>
            <ActionIcon
              variant="default"
              size="sm"
              onClick={() => go(-1)}
              disabled={siblings.length <= 1 || index === 0}
              aria-label="Previous card"
            >
              <IconChevronLeft size={14} />
            </ActionIcon>
            <Text size="xs" c="dimmed">
              Card {siblings.length ? index + 1 : 1} of {Math.max(1, siblings.length)}
            </Text>
            <ActionIcon
              variant="default"
              size="sm"
              onClick={() => go(1)}
              disabled={siblings.length <= 1 || index >= siblings.length - 1}
              aria-label="Next card"
            >
              <IconChevronRight size={14} />
            </ActionIcon>
          </Group>
        </Group>
      </Stack>
    </Modal>
  );
}
